import { EventOperationType } from '@/api/apis/route';

export enum HubTypeEnum {
  DepartureHub = 'DepartureHub',
  ArrivalHub = 'ArrivalHub',
}

export type LegIdentifiers = {
  logisticsId: string;
  routeId: string;
  legId: string;
};

export type LogisticsVersionIdentifier = {
  version: number;
};

export type RouteIdentifiers = Omit<LegIdentifiers, 'legId'>;
export type HubIdentifiers = Omit<LegIdentifiers, 'legId'> & { hubId: string };
export type ResourceIdentifiers = Pick<LegIdentifiers, 'logisticsId'> & { resourceId: string };

export type RemoveRouteLegsParams = RouteIdentifiers &
  LogisticsVersionIdentifier & {
    legIds: string[];
  };

export type RemoveRouteLegsData = Pick<LegIdentifiers, 'routeId'> &
  LogisticsVersionIdentifier & {
    legIds: string[];
  };

export type RemoveLogisticsRoutesParams = Pick<LegIdentifiers, 'logisticsId'> & {
  routes: RemoveRouteLegsData[];
};

type BaseHubsDataRequestList = Pick<LegIdentifiers, 'routeId'> &
  LogisticsVersionIdentifier & {
    type: HubTypeEnum;
  };

export type HubsDataRequestList = Pick<LegIdentifiers, 'routeId'> &
  LogisticsVersionIdentifier & {
    type: HubTypeEnum;
    hubId: string;
  };

export type HubsRequestParams<T = BaseHubsDataRequestList> = Pick<LegIdentifiers, 'logisticsId'> & {
  hubs: T[];
};

export type AddLegParams<T> = RouteIdentifiers &
  LogisticsVersionIdentifier & {
    data: T;
  };

export type UpdateLegParams<T> = LegIdentifiers &
  LogisticsVersionIdentifier & {
    data: Partial<T>;
  };

// Logs
export type BaseEventFieldLog<T> = {
  operationType: EventOperationType;
  previousValue: T | null;
  newValue: T | null;
};

export type RemovedOrNoChangeEventFieldLog<T> = BaseEventFieldLog<T> & {
  operationType: EventOperationType.NoChange | EventOperationType.Removed;
  previousValue: T;
  newValue: null;
};

export type ModifiedEventFieldLog<T> = BaseEventFieldLog<T> & {
  operationType: EventOperationType.Modified;
  previousValue: T;
  newValue: T;
};

export type AddedEventFieldLog<T> = BaseEventFieldLog<T> & {
  operationType: EventOperationType.Added;
  previousValue: null;
  newValue: T;
};

export type EventFieldLog<T> = AddedEventFieldLog<T> | ModifiedEventFieldLog<T> | RemovedOrNoChangeEventFieldLog<T>;

type Primitive = string | number | boolean | bigint | symbol | null | undefined;

// Handles one field (recursively), respects exception keys
export type WrappedField<T = unknown, ExceptionKeys extends PropertyKey = never> = T extends Primitive
  ? EventFieldLog<T>
  : T extends Array<infer U>
    ? // Array → wrap items (not array)
      ModifiedEventLog<U, ExceptionKeys>[]
    : T extends object
      ? // Object → wrap recursively unless it's an exception
        ModifiedEventLog<T, ExceptionKeys>
      : EventFieldLog<T>;

// Wraps each property, skipping recursion for exception keys
export type ModifiedEventLog<T = unknown, ExceptionKeys extends PropertyKey = never> = {
  [K in keyof T]: K extends ExceptionKeys // If field name is an exception
    ? // Primitive field → leave as-is
      T[K] extends Primitive
      ? T[K]
      : // Array → keep as-is
        T[K] extends Array<unknown>
        ? T[K]
        : // Object → wrap once, no recursion
          EventFieldLog<T[K]>
    : // Default → recursive wrapping
      WrappedField<T[K], ExceptionKeys>;
};
